import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { api } from '../../../lib/api';
import B2BSubscriptionsPanel from './B2BSubscriptionsPanel';

type RevenuePoint = { bucket: string; amount: number; count: number };
type RevenueResponse = {
  mode?: 'test' | 'live';
  currency: string;
  interval: 'day' | 'month' | 'year';
  date_from: string;
  date_to: string;
  total_amount: number;
  total_count: number;
  series: RevenuePoint[];
};

type KpisResponse = {
  mode: 'test' | 'live';
  db: { total: number; active: number; basic_active: number; full_active: number };
  stripe: { active: number; cancel_at_period_end: number; mrr_amount: number; currency: string };
};

type PlatformSubscriptionRow = {
  id: number;
  owner_user_id: number;
  owner_email: string;
  owner_full_name: string;
  plan: string;
  status: string;
  expires_at: string | null;
  child_seats_limit: number;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_status: string | null;
  created_at: string | null;
};

const intervalOptions = [
  { value: 'day', label: 'Zi' },
  { value: 'month', label: 'Lună' },
  { value: 'year', label: 'An' },
];

function formatMoneyCents(amount: number, currency: string) {
  const cur = (currency || '').toUpperCase();
  if (!Number.isFinite(amount)) return '—';
  const v = amount / 100;
  try {
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: cur === 'N/A' ? 'EUR' : cur }).format(v);
  } catch {
    return `${v.toFixed(2)} ${cur || ''}`.trim();
  }
}

function isoDate(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function filenameFromHeader(header: string | undefined, fallback: string) {
  const h = String(header || '');
  const m = /filename=\"?([^\";]+)\"?/i.exec(h);
  return (m?.[1] || '').trim() || fallback;
}

async function downloadCsv(path: string, params: Record<string, any> | undefined, fallbackName: string) {
  const resp = await api.get(path, { params, responseType: 'blob' as any });
  const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFromHeader((resp.headers as any)?.['content-disposition'], fallbackName);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function PaymentsReportsPanel() {
  const [loading, setLoading] = useState(false);
  const [interval, setInterval] = useState<'day' | 'month' | 'year'>('day');
  const [dateFrom, setDateFrom] = useState(() => isoDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [dateTo, setDateTo] = useState(() => isoDate(new Date()));
  const [revenue, setRevenue] = useState<RevenueResponse | null>(null);
  const [kpis, setKpis] = useState<KpisResponse | null>(null);
  const [stripeMode, setStripeMode] = useState<'test' | 'live'>('test');

  const loadRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const [kpisResp, revResp] = await Promise.all([
        api.get('/api/billing/platform/kpis'),
        api.get('/api/billing/platform/revenue', {
        params: { date_from: dateFrom, date_to: dateTo, interval },
        }),
      ]);
      setKpis(kpisResp.data as any);
      setRevenue(revResp.data as any);
      const mode = String((revResp.data as any)?.mode || (kpisResp.data as any)?.mode || 'test') as any;
      setStripeMode(mode === 'live' ? 'live' : 'test');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca rapoartele Stripe.');
      setRevenue(null);
      setKpis(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, interval]);

  useEffect(() => {
    void loadRevenue();
  }, [loadRevenue]);

  const maxPoint = useMemo(() => {
    const points = revenue?.series || [];
    return points.reduce((mx, p) => Math.max(mx, Number(p.amount || 0)), 0);
  }, [revenue?.series]);

  const [subsLoading, setSubsLoading] = useState(false);
  const [subQ, setSubQ] = useState('');
  const [subPlan, setSubPlan] = useState<string>('');
  const [subStatus, setSubStatus] = useState<string>('');
  const [subs, setSubs] = useState<PlatformSubscriptionRow[]>([]);

  const loadSubs = useCallback(async () => {
    setSubsLoading(true);
    try {
      const resp = await api.get('/api/billing/platform/subscriptions', {
        params: { q: subQ || undefined, plan: subPlan || undefined, status: subStatus || undefined },
      });
      setSubs(Array.isArray(resp.data) ? (resp.data as any) : []);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca abonamentele.');
      setSubs([]);
    } finally {
      setSubsLoading(false);
    }
  }, [subQ, subPlan, subStatus]);

  const stripeDashboardBase = stripeMode === 'live' ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test';

  useEffect(() => {
    void loadSubs();
  }, [loadSubs]);

  const cancelStripe = useCallback(
    async (row: PlatformSubscriptionRow, immediate: boolean) => {
      const msg = immediate
        ? `Anulezi IMEDIAT abonamentul Stripe pentru ${row.owner_email}?`
        : `Oprești reînnoirea (la final de perioadă) pentru ${row.owner_email}?`;
      const ok = window.confirm(msg);
      if (!ok) return;
      try {
        await api.post('/api/billing/platform/cancel', { owner_user_id: row.owner_user_id, immediate });
        await loadSubs();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut anula abonamentul în Stripe.');
      }
    },
    [loadSubs]
  );

  const grantFree = useCallback(
    async (row: PlatformSubscriptionRow, plan: 'free' | 'basic' | 'full', months: number) => {
      const ok = window.confirm(`Acordezi planul ${plan.toUpperCase()} pentru ${months} lună(i) utilizatorului ${row.owner_email}?`);
      if (!ok) return;
      try {
        await api.post('/api/subscriptions/admin/set', { user_id: row.owner_user_id, plan, months });
        await loadSubs();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut acorda abonamentul.');
      }
    },
    [loadSubs]
  );

  const grantUnrestricted = useCallback(
    async (row: PlatformSubscriptionRow, enabled: boolean) => {
      const ok = window.confirm(
        enabled
          ? `Activezi licență (fără restricții) pentru ${row.owner_email}?`
          : `Dezactivezi licența (fără restricții) pentru ${row.owner_email}?`
      );
      if (!ok) return;
      try {
        await api.put('/api/admin/platform/access-grants', {
          scope_type: 'user',
          scope_id: row.owner_user_id,
          kind: 'unrestricted',
          enabled,
          note: 'Setat din rapoarte plăți',
        });
        alert('OK.');
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut salva licența.');
      }
    },
    []
  );

  const exportInvoices = useCallback(async () => {
    try {
      await downloadCsv(
        '/api/billing/platform/invoices.csv',
        { date_from: dateFrom, date_to: dateTo },
        `stripe_invoices_paid_${dateFrom}_${dateTo}.csv`
      );
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta CSV.');
    }
  }, [dateFrom, dateTo]);

  const exportSubscriptions = useCallback(async () => {
    try {
      await downloadCsv(
        '/api/billing/platform/subscriptions.csv',
        { q: subQ || undefined, plan: subPlan || undefined, status: subStatus || undefined },
        'subscriptions_db.csv'
      );
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta CSV.');
    }
  }, [subQ, subPlan, subStatus]);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold text-foreground">Încasări Stripe (B2C)</div>
            <div className="text-sm text-muted-foreground">Totaluri pe zi / lună / an (invoice-uri plătite).</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" iconName="Download" disabled={loading} onClick={() => void exportInvoices()}>
              Export CSV
            </Button>
            <Button variant="outline" iconName="RefreshCw" onClick={() => void loadRevenue()} disabled={loading}>
              Reîncarcă
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <Select
            label="Interval"
            value={interval}
            options={intervalOptions}
            onChange={(v) => setInterval((v as any) || 'day')}
          />
          <Input label="De la" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <Input label="Până la" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          <div className="flex items-end gap-2">
            <Button variant="default" onClick={() => void loadRevenue()} disabled={loading}>
              Aplică
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setInterval('day');
                setDateFrom(isoDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)));
                setDateTo(isoDate(new Date()));
              }}
            >
              7 zile
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setInterval('day');
                setDateFrom(isoDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
                setDateTo(isoDate(new Date()));
              }}
            >
              30 zile
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">MRR (Stripe)</div>
            <div className="text-lg font-semibold text-foreground">
              {formatMoneyCents(kpis?.stripe?.mrr_amount || 0, kpis?.stripe?.currency || 'N/A')}
            </div>
            <div className="text-xs text-muted-foreground">Active: {kpis?.stripe?.active ?? 0}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="text-lg font-semibold text-foreground">
              {formatMoneyCents(revenue?.total_amount || 0, revenue?.currency || 'N/A')}
            </div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Tranzacții</div>
            <div className="text-lg font-semibold text-foreground">{revenue?.total_count ?? 0}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Monedă</div>
            <div className="text-lg font-semibold text-foreground">{revenue?.currency || '—'}</div>
            <div className="text-xs text-muted-foreground">Mod: {stripeMode.toUpperCase()}</div>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {(revenue?.series || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">Nu există date în perioada selectată.</div>
          ) : null}

          {(revenue?.series || []).slice(-60).map((p) => {
            const width = maxPoint > 0 ? Math.max(2, Math.round((Number(p.amount || 0) / maxPoint) * 100)) : 2;
            return (
              <div key={p.bucket} className="flex items-center gap-3">
                <div className="w-28 text-xs text-muted-foreground">{p.bucket}</div>
                <div className="flex-1 h-2 bg-muted rounded">
                  <div className="h-2 bg-primary rounded" style={{ width: `${width}%` }} />
                </div>
                <div className="w-40 text-xs text-foreground text-right">
                  {formatMoneyCents(Number(p.amount || 0), revenue?.currency || 'N/A')} ({p.count})
                </div>
              </div>
            );
          })}
          {(revenue?.series || []).length > 60 ? (
            <div className="text-xs text-muted-foreground">Afișăm ultimele 60 puncte din grafic.</div>
          ) : null}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold text-foreground">Gestionare abonamente</div>
            <div className="text-sm text-muted-foreground">Caută utilizator, anulează în Stripe, acordă planuri gratuite, licențe.</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" iconName="Download" disabled={subsLoading} onClick={() => void exportSubscriptions()}>
              Export CSV
            </Button>
            <Button variant="outline" iconName="RefreshCw" onClick={() => void loadSubs()} disabled={subsLoading}>
              Reîncarcă
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-4">
          <Input label="Caută" placeholder="Email / nume..." value={subQ} onChange={(e) => setSubQ(e.target.value)} />
          <Select
            label="Plan"
            value={subPlan}
            options={[
              { value: '', label: 'Toate' },
              { value: 'basic', label: 'Basic' },
              { value: 'full', label: 'Full' },
              { value: 'free', label: 'Free' },
            ]}
            onChange={(v) => setSubPlan(String(v || ''))}
            clearable
          />
          <Select
            label="Status"
            value={subStatus}
            options={[
              { value: '', label: 'Toate' },
              { value: 'active', label: 'Active' },
              { value: 'canceled', label: 'Canceled' },
              { value: 'expired', label: 'Expired' },
            ]}
            onChange={(v) => setSubStatus(String(v || ''))}
            clearable
          />
          <div className="flex items-end">
            <Button variant="default" iconName="Search" onClick={() => void loadSubs()} disabled={subsLoading}>
              Caută
            </Button>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {subs.length === 0 ? <div className="text-sm text-muted-foreground">Nu există abonamente.</div> : null}

          {subs.slice(0, 300).map((row) => (
            <div key={row.id} className="border border-border rounded-lg p-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-foreground truncate">
                  {row.owner_full_name} <span className="text-xs text-muted-foreground">({row.owner_email})</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Plan: <span className="text-foreground">{String(row.plan || '').toUpperCase()}</span> • Status:{' '}
                  <span className="text-foreground">{row.status}</span>
                  {row.expires_at ? ` • Expiră: ${new Date(row.expires_at).toLocaleString('ro-RO')}` : ''}
                  {row.stripe_status ? ` • Stripe: ${row.stripe_status}` : ''}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {row.stripe_subscription_id ? (
                    <a
                      className="text-xs text-primary hover:underline"
                      href={`${stripeDashboardBase}/subscriptions/${encodeURIComponent(row.stripe_subscription_id)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Deschide abonamentul în Stripe
                    </a>
                  ) : null}
                  {row.stripe_customer_id ? (
                    <a
                      className="text-xs text-primary hover:underline"
                      href={`${stripeDashboardBase}/customers/${encodeURIComponent(row.stripe_customer_id)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Client Stripe
                    </a>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {row.stripe_subscription_id ? (
                  <>
                    <Button variant="outline" size="sm" iconName="Ban" onClick={() => void cancelStripe(row, false)}>
                      Oprește reînnoirea
                    </Button>
                    <Button variant="destructive" size="sm" iconName="Trash2" onClick={() => void cancelStripe(row, true)}>
                      Anulează acum
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Info" size={14} className="text-muted-foreground" />
                    Fără Stripe ID
                  </span>
                )}

                <Button variant="outline" size="sm" iconName="Gift" onClick={() => void grantFree(row, 'basic', 1)}>
                  Basic 1 lună
                </Button>
                <Button variant="outline" size="sm" iconName="Gift" onClick={() => void grantFree(row, 'full', 1)}>
                  Full 1 lună
                </Button>
                <Button variant="outline" size="sm" iconName="BadgeCheck" onClick={() => void grantUnrestricted(row, true)}>
                  Licență ON
                </Button>
                <Button variant="outline" size="sm" iconName="BadgeX" onClick={() => void grantUnrestricted(row, false)}>
                  Licență OFF
                </Button>
              </div>
            </div>
          ))}

          {subs.length > 300 ? (
            <div className="text-xs text-muted-foreground">Afișăm primele 300 rezultate. Folosește filtrarea pentru a restrânge lista.</div>
          ) : null}
        </div>
      </div>

      <B2BSubscriptionsPanel />
    </div>
  );
}
